import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Check } from "lucide-react";
import { SERVICES, BUSINESS } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const empty = { name: "", email: "", phone: "", service: SERVICES[0].title, city: BUSINESS.areas[0], message: "" };

export default function QuoteForm({ compact = false }) {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      setForm(empty);
      toast.success("Thanks! We'll be in touch within one business day.");
    } catch (err) {
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "peer w-full border-0 border-b border-[var(--line)] bg-transparent px-0 py-3 text-base text-[var(--ink)] outline-none transition-colors duration-300 focus:border-[var(--brand)] placeholder:text-[var(--ink-soft)]/50";
  const labelCls = "overline mb-2 block text-[var(--ink-soft)]";

  if (done) {
    return (
      <div data-testid="quote-success" className="flex flex-col items-start gap-4 border border-[var(--line)] bg-[var(--brand-light)] p-10">
        <div className="grid h-14 w-14 place-items-center rounded-full bg-[var(--brand)] text-white">
          <Check className="h-7 w-7" />
        </div>
        <h3 className="font-display text-3xl font-extrabold tracking-tight">Request received</h3>
        <p className="max-w-md text-[var(--ink-soft)]">
          Thank you for reaching out to Magical Cleaning Team. A friendly team member will contact you within one
          business day with your free quote.
        </p>
        <button onClick={() => setDone(false)} data-testid="quote-reset" className="nav-link text-sm font-semibold uppercase tracking-[0.14em] text-[var(--brand)]">
          Send another request
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="quote-form" className="grid gap-7">
      <div className={`grid gap-7 ${compact ? "" : "sm:grid-cols-2"}`}>
        <div>
          <label className={labelCls}>Full name</label>
          <input data-testid="quote-name" value={form.name} onChange={set("name")} className={inputCls} placeholder="Jane Doe" />
        </div>
        <div>
          <label className={labelCls}>Phone</label>
          <input data-testid="quote-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="(519) 000-0000" />
        </div>
      </div>

      <div>
        <label className={labelCls}>Email</label>
        <input data-testid="quote-email" type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="jane@email.com" />
      </div>

      <div className={`grid gap-7 ${compact ? "" : "sm:grid-cols-2"}`}>
        <div>
          <label className={labelCls}>Service</label>
          <select data-testid="quote-service" value={form.service} onChange={set("service")} className={inputCls}>
            {SERVICES.map((s) => (
              <option key={s.id} value={s.title}>{s.title}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>City</label>
          <select data-testid="quote-city" value={form.city} onChange={set("city")} className={inputCls}>
            {BUSINESS.areas.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className={labelCls}>Tell us about your space (optional)</label>
        <textarea data-testid="quote-message" value={form.message} onChange={set("message")} rows={3} className={inputCls} placeholder="3 bed, 2 bath, move-out clean needed for June 30…" />
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="quote-submit"
        className="sweep-btn mt-2 inline-flex items-center justify-center gap-2 bg-[var(--brand)] px-8 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
      >
        <span className="relative z-10 flex items-center gap-2">
          {loading && <Loader2 className="h-4 w-4 animate-spin" />}
          {loading ? "Sending…" : "Get My Free Quote"}
        </span>
      </button>
    </form>
  );
}
